import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import CustomSlider from '../../UI/CustomSlider';
import MediaButton from './MediaButton';
import MediaSelectorModal from './MediaSelectorModal';
import FontPicker from '../../UI/FontPicker';
import { ensureGoogleFontsStylesheet } from '../../../utils/googleFonts';
import { ensureFontLoaded } from '../../../utils/ensureFontLoaded';
import CaptionControlsSection from './CaptionControlsSection';
import TextPropertiesSection from './TextPropertiesSection';
import {
  generateCaptions as generateCaptionsUtil,
  handleTranscriptChange as handleTranscriptChangeUtil,
  updateCaptionStyle as updateCaptionStyleUtil,
} from '../../../utils/captionUtils';


const AgComponentProperties = ({ element, onPropertyChange, expandedSections, onToggleSection, onSliderStart, onSliderEnd }) => {
  const modalRef = useRef(null);
  const [isGeneratingCaption, setIsGeneratingCaption] = React.useState(false);
  const [textContentInput, setTextContentInput] = React.useState('');
  const [isTextContentFocused, setIsTextContentFocused] = React.useState(false);
  const [transcriptInput, setTranscriptInput] = React.useState('');
  const [isTranscriptFocused, setIsTranscriptFocused] = React.useState(false);
  const [isCaptionPopupOpen, setIsCaptionPopupOpen] = React.useState(false);
  const [isTextPopupOpen, setIsTextPopupOpen] = React.useState(false);
  const transcriptTextareaRef = useRef(null);
  const captionPopupAnchorRef = useRef(null);

  React.useEffect(() => {
    // Inject Google Fonts on mount for Presets preview
    ensureGoogleFontsStylesheet();
  }, []);

  React.useEffect(() => {
    if (transcriptTextareaRef.current) {
      transcriptTextareaRef.current.style.height = 'auto';
      transcriptTextareaRef.current.style.height = `${transcriptTextareaRef.current.scrollHeight}px`;
    }
  }, [transcriptInput, element?.componentData?.captions?.phrases, isTranscriptFocused]);

  const generateCaptions = async (mediaUrl) => {
    await generateCaptionsUtil({
      mediaUrl,
      element,
      onPropertyChange,
      setIsGeneratingCaption,
    });
  };

  const updateCaptionStyle = (styleUpdate, isPreset = false) => {
    updateCaptionStyleUtil({
      element,
      onPropertyChange,
      styleUpdate,
      isPreset,
    });
  };

  const handleTranscriptChange = (newFullText) => {
    handleTranscriptChangeUtil({
      newFullText,
      element,
      onPropertyChange,
    });
  };

  const updateTextProp = (prop, value) => {
    onPropertyChange(`componentData.${prop}`, value);
    const sprite = element.pixiSprite;
    if (sprite) {
      const applyTextChangeToSprite = () => {
        if (!sprite || sprite.destroyed || sprite.scale === null) return;

        if (prop === 'text') sprite.text = value;
        if (sprite.style) {
          if (prop === 'fontFamily') sprite.style.fontFamily = value;
          if (prop === 'fontSize') {
            sprite.style.fontSize = value;
            // Refresh resolution so manual font size changes stay crisp
            sprite.style.resolution = Math.max(2, window.devicePixelRatio || 2);
          }
          if (prop === 'color') sprite.style.fill = value;
        }

        // Rebuild interactive area when text dimensions change
        if ((prop === 'text' || prop === 'fontSize' || prop === 'fontFamily') && sprite.interactiveArea) {
          const textWidth = sprite.width;
          const textHeight = sprite.height;
          sprite.interactiveArea.clear();
          sprite.interactiveArea.rect(-textWidth / 2, -textHeight / 2, textWidth, textHeight);
          sprite.interactiveArea.fill(0x000000, 0);
        }
      };

      applyTextChangeToSprite();

      // PIXI text can render fallback fonts if we update before font is active.
      // Re-apply after preload so selected web fonts reliably take effect.
      if (prop === 'fontFamily') {
        const requestedFont = value;
        ensureFontLoaded(value)
          .then(() => {
            if (!sprite || sprite.destroyed || sprite.scale === null || !sprite.style) return;
            if (sprite.style.fontFamily !== requestedFont) return;
            applyTextChangeToSprite();
          })
          .catch(() => {
            // Keep fallback rendering when font preload fails.
          });
      }
    }
  };

  if (!element) return null;

  // Helper to extract field info from array format
  const extractFields = () => {
    if (!Array.isArray(element.componentData?.fields)) return [];

    return element.componentData.fields
      .map((field, index) => {
        if (!field) return null;

        // Find the actual media/data field key instead of implicitly relying on object insertion order
        const fieldType = Object.keys(field).find(key =>
          ['textField', 'imageField', 'videoField', 'audioField', 'colorField'].includes(key)
        );

        if (!fieldType) return null;
        const value = field[fieldType];

        // Determine the category based on field type
        let category = 'other';
        if (fieldType.includes('text')) category = 'text';
        else if (fieldType.includes('color')) category = 'color';
        else if (fieldType.includes('image')) category = 'image';
        else if (fieldType.includes('video')) category = 'video';
        else if (fieldType.includes('audio')) category = 'audio';

        // Capture target dimensions & duration from the NexJob asset
        const targetWidth = field.width || null;
        const targetHeight = field.height || null;
        const duration = field.duration || null;

        return { index, fieldType, value, category, targetWidth, targetHeight, duration };
      })
      .filter(Boolean); // Remove null entries
  };

  // Field renderers for each category - each handles its own layout and rendering
  const fieldRenderers = {
    text: (fields) => (
      <div className="space-y-4">
        {fields.map(({ index, fieldType, value }) => {
          const baseInputProps = {
            value: value || '',
            onChange: (e) => onPropertyChange(`componentData.fields[${index}].${fieldType}`, e.target.value),
            className: "w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          };

          return (
            <div className="space-y-3" key={`${fieldType}-${index}`}>
              <div>
                <label className="block text-xs font-medium text-white mb-1">
                  Text Content
                </label>
                <textarea {...baseInputProps} rows="3" />
              </div>

              {/* Additional text properties */}
              {/* <div>
                <label className="block text-xs font-medium text-white mb-1">Font Size</label>
                <input
                  type="number"
                  value={element.componentData?.fields?.[index]?.[`${fieldType}_fontSize`] || 24}
                  onChange={(e) => onPropertyChange(`componentData.fields[${index}].${fieldType}_fontSize`, parseInt(e.target.value) || 24)}
                  className={baseInputProps.className}
                />
              </div> */}

              <div>
                <label className="block text-xs font-medium text-white mb-1">Font Family</label>
                <FontPicker
                  value={element.componentData?.fields?.[index]?.[`${fieldType}_fontFamily`] || 'Arial'}
                  onChange={(fontValue) => onPropertyChange(`componentData.fields[${index}].${fieldType}_fontFamily`, fontValue)}
                  className={baseInputProps.className}
                />
              </div>

              {/* <div>
                <label className="block text-xs font-medium text-white mb-1">Color</label>
                <input
                  type="color"
                  value={element.componentData?.fields?.[index]?.[`${fieldType}_color`] || '#000000'}
                  onChange={(e) => onPropertyChange(`componentData.fields[${index}].${fieldType}_color`, e.target.value)}
                  className="w-full h-8 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div> */}
            </div>
          );
        })}
      </div>
    ),

    color: (fields) => (
      <div className="grid grid-cols-5 gap-2">
        {fields.map(({ index, fieldType, value }) => (
          <label key={`color-${index}`} className="cursor-pointer">
            <input
              type="color"
              value={value || '#000000'}
              onChange={(e) => onPropertyChange(`componentData.fields[${index}].${fieldType}`, e.target.value)}
              className="sr-only"
            />
            <div
              className="w-12 h-12 rounded border-2 border-gray-400 hover:border-ag-green transition-all"
              style={{ backgroundColor: value || '#000000' }}
              title={value || '#000000'}
            />
          </label>
        ))}
      </div>
    ),

    image: (fields) => (
      <div className="flex flex-wrap gap-3">
        {fields.map(({ index, fieldType, value, targetWidth, targetHeight }) => (
          <MediaButton
            key={`${fieldType}-${index}`}
            value={value}
            mediaType="image"
            index={index}
            onClick={() => {
              if (modalRef.current) {
                modalRef.current.open('image', value, (result) => {
                  if (typeof result === 'string') {
                    // Plain URL — no crop/trim edits
                    onPropertyChange(`componentData.fields[${index}].${fieldType}`, result);
                  } else if (result && result.url) {
                    onPropertyChange(`componentData.fields[${index}].${fieldType}`, result.url);
                  }
                }, {
                  layerKey: `${element.id}_${index}`,
                  targetWidth: targetWidth || undefined,
                  targetHeight: targetHeight || undefined,
                });
              }
            }}
          />
        ))}
      </div>
    ),

    video: (fields) => (
      <div className="flex flex-wrap gap-3">
        {fields.map(({ index, fieldType, value, targetWidth, targetHeight, duration }) => (
          <div key={`${fieldType}-${index}`} className="w-full">
            <MediaButton
              value={value}
              mediaType="video"
              index={index}
              onClick={() => {
                if (modalRef.current) {
                  modalRef.current.open('video', value, (result) => {
                    if (typeof result === 'string') {
                      onPropertyChange(`componentData.fields[${index}].${fieldType}`, result);
                    } else if (result && result.url) {
                      onPropertyChange(`componentData.fields[${index}].${fieldType}`, result.url);
                    }
                  }, {
                    targetWidth: targetWidth || undefined,
                    targetHeight: targetHeight || undefined,
                    maxDuration: duration || undefined,
                  });
                }
              }}
            />
            <CaptionControlsSection
              element={element}
              value={value}
              index={index}
              fieldType={fieldType}
              mediaCategory="video"
              isGeneratingCaption={isGeneratingCaption}
              transcriptInput={transcriptInput}
              setTranscriptInput={setTranscriptInput}
              isTranscriptFocused={isTranscriptFocused}
              setIsTranscriptFocused={setIsTranscriptFocused}
              transcriptTextareaRef={transcriptTextareaRef}
              isCaptionPopupOpen={isCaptionPopupOpen}
              setIsCaptionPopupOpen={setIsCaptionPopupOpen}
              captionPopupAnchorRef={captionPopupAnchorRef}
              onGenerateCaptions={generateCaptions}
              onUpdateCaptionStyle={updateCaptionStyle}
              onTranscriptChange={handleTranscriptChange}
            />
          </div>
        ))}
      </div>
    ),

    audio: (fields) => (
      <div className="flex flex-wrap gap-3">
        {fields.map(({ index, fieldType, value, duration }) => (
          <div key={`${fieldType}-${index}`} className="w-full">
            <MediaButton
              value={value}
              mediaType="audio"
              index={index}
              onClick={() => {
                if (modalRef.current) {
                  modalRef.current.open('audio', value, (url) => {
                    onPropertyChange(`componentData.fields[${index}].${fieldType}`, url);
                  }, {
                    maxDuration: duration || undefined,
                  });
                }
              }}
            />
            <CaptionControlsSection
              element={element}
              value={value}
              index={index}
              fieldType={fieldType}
              mediaCategory="audio"
              isGeneratingCaption={isGeneratingCaption}
              transcriptInput={transcriptInput}
              setTranscriptInput={setTranscriptInput}
              isTranscriptFocused={isTranscriptFocused}
              setIsTranscriptFocused={setIsTranscriptFocused}
              transcriptTextareaRef={transcriptTextareaRef}
              isCaptionPopupOpen={isCaptionPopupOpen}
              setIsCaptionPopupOpen={setIsCaptionPopupOpen}
              captionPopupAnchorRef={captionPopupAnchorRef}
              onGenerateCaptions={generateCaptions}
              onUpdateCaptionStyle={updateCaptionStyle}
              onTranscriptChange={handleTranscriptChange}
            />
          </div>
        ))}
      </div>
    ),

    // Default renderer for unknown field types
    default: (fields) => (
      <div className="space-y-4">
        {fields.map(({ index, fieldType, value }) => (
          <div key={`${fieldType}-${index}`}>
            <label className="block text-xs font-medium text-white mb-1">
              Field {index + 1}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onPropertyChange(`componentData.fields[${index}].${fieldType}`, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        ))}
      </div>
    )
  };

  // For General properties like opacity, volume, mute
  const renderGeneralProperties = () => {
    const hasVideo = element.pixiSprite?.videoElement;
    const hasAudio = element.pixiSprite?.soundId;
    const hasMediaControls = hasVideo || hasAudio;

    return (
      <div className="space-y-3 px-3 py-3">
        <CustomSlider
          label="Opacity"
          value={(element.alpha !== undefined ? element.alpha : 1) * 100}
          onChange={(value) => onPropertyChange('alpha', value / 100)}
          min={0}
          max={100}
          valueFormatter={(val) => `${Math.round(val)}%`}
          onSliderStart={onSliderStart}
          onSliderEnd={onSliderEnd}
        />

        {hasMediaControls && (
          <>
            <CustomSlider
              label="Volume"
              value={element.muted ? 0 : ((element.volume !== undefined ? element.volume : 1) * 100)}
              onChange={(value) => {
                const newVolume = value / 100;
                onPropertyChange('volume', newVolume);
                if (newVolume > 0 && element.muted) {
                  onPropertyChange('muted', false);
                }
              }}
              min={0}
              max={100}
              valueFormatter={(val) => `${Math.round(val)}%`}
              onSliderStart={onSliderStart}
              onSliderEnd={onSliderEnd}
            />

            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-white">Mute</label>
              <button
                onClick={() => onPropertyChange('muted', !element.muted)}
                className={`px-3 py-1 text-xs rounded transition-colors ${element.muted
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-ag-green hover:bg-green-600 text-black'
                  }`}
              >
                {element.muted ? 'Unmute' : 'Mute'}
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <label className="text-xs font-medium text-white">Frame Properties</label>
              <div className="flex flex-col space-y-4">
                <label className="flex items-center space-x-2 text-xs text-white">
                  <input
                    type="radio"
                    name="playbackMode"
                    value="pause"
                    checked={element.playbackMode === 'pause'}
                    onChange={() => onPropertyChange('playbackMode', 'pause')}
                    className="h-4 w-4 rounded-full accent-ag-green"
                  />
                  <span>Pause at last frame</span>
                </label>
                <label className="flex items-center space-x-2 text-xs text-white">
                  <input
                    type="radio"
                    name="playbackMode"
                    value="loop"
                    checked={element.playbackMode === 'loop' || !element.playbackMode}
                    onChange={() => onPropertyChange('playbackMode', 'loop')}
                    className="h-4 w-4 rounded-full accent-ag-green"
                  />
                  <span>Loop</span>
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderCollapsibleSection = (title, isExpanded, onToggle, children, count = 0) => (
    <div className="">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 text-left text-xs font-medium text-white border-b border-ag-black-600 hover:bg-ag-black-800 flex items-center justify-between"
      >
        <span className="flex items-center">{title}</span>
        <span className="flex">
          {count > 0 && (
            <span className="px-2 py-0.5 text-xs text-ag-green rounded-full">{count}</span>
          )}
          <svg
            className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isExpanded && (
        <div className="p-3">{children}</div>
      )}
    </div>
  );

  const renderComponentFields = () => {
    // Render normal property controls if it's not a component with fields
    if (!element.componentData?.fields) {
      // If it's a plain text element, show text editing properties
      if (element.type === 'text') {
        return (
          <TextPropertiesSection
            element={element}
            isTextContentFocused={isTextContentFocused}
            setIsTextContentFocused={setIsTextContentFocused}
            textContentInput={textContentInput}
            setTextContentInput={setTextContentInput}
            isTextPopupOpen={isTextPopupOpen}
            setIsTextPopupOpen={setIsTextPopupOpen}
            onUpdateTextProp={updateTextProp}
          />
        );
      }

      // If it's a plain video or audio canvas element
      const isVideo = element.type === 'video';
      const isAudio = element.type === 'audio';

      if (isVideo || isAudio) {
        return (
          <div className="px-3 pb-3">
            <CaptionControlsSection
              element={element}
              value={element.componentData?.src || element.componentData?.url || element.url || element.src}
              index={0}
              fieldType={element.type}
              mediaCategory={element.type}
              isGeneratingCaption={isGeneratingCaption}
              transcriptInput={transcriptInput}
              setTranscriptInput={setTranscriptInput}
              isTranscriptFocused={isTranscriptFocused}
              setIsTranscriptFocused={setIsTranscriptFocused}
              transcriptTextareaRef={transcriptTextareaRef}
              isCaptionPopupOpen={isCaptionPopupOpen}
              setIsCaptionPopupOpen={setIsCaptionPopupOpen}
              captionPopupAnchorRef={captionPopupAnchorRef}
              onGenerateCaptions={generateCaptions}
              onUpdateCaptionStyle={updateCaptionStyle}
              onTranscriptChange={handleTranscriptChange}
            />
          </div>
        );
      }
      return null;
    }

    // Extract and group fields
    const fields = extractFields();
    const groupedFields = fields.reduce((acc, field) => {
      if (!acc[field.category]) acc[field.category] = [];
      acc[field.category].push(field);
      return acc;
    }, {});

    // Define section titles
    const sectionTitles = {
      text: 'Text Fields',
      color: 'Color Fields',
      image: 'Image Fields',
      video: 'Video Fields',
      audio: 'Audio Fields'
    };

    return (
      <div className="space-y-2">
        {/* Render all field sections using their specific renderers */}
        {Object.entries(groupedFields).map(([category, categoryFields]) => {
          const renderer = fieldRenderers[category] || fieldRenderers.default;

          return categoryFields.length > 0 && renderCollapsibleSection(
            sectionTitles[category] || `${category} Fields`,
            expandedSections[category],
            () => onToggleSection(category),
            renderer(categoryFields),
            categoryFields.length
          );
        })}
      </div>
    );
  };

  return (
    <div className="border-gray-200">
      <h4 className="pl-3 text-sm font-medium text-ag-black-300 mb-3 truncate">
        {element.name}
      </h4>
      {renderComponentFields()}
      {renderGeneralProperties()}

      {/* Media Selector Modal — rendered via portal to appear above AiDialog */}
      {ReactDOM.createPortal(
        <MediaSelectorModal ref={modalRef} />,
        document.body
      )}
    </div>
  );
};

export default AgComponentProperties;

